import React from "react";

import { Navigate, Route, Routes } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";

import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";

import Appbar from "./components/drawer/Appbar";
import AppDrawer from "./components/drawer/AppDrawer";

import MenuPage from "../menu/MenuPage";
import ChatPage from "../chat/ChatPage";
import FavoritesPage from "../favorites/FavoritesPage";
import LoginPage from "../auth/LoginPage";
import AdminSettings from "../admin/AdminSettings";
import LogOut from "../auth/LogOut";
import WelcomePrefs from "../prefs/WelcomePrefs";

import NoMatch from "../../helper-components/pages/NoMatch";
import Copyright from "../../helper-components/misc/Copyright";
import UnexpectedError from "../../helper-components/pages/UnexpectedError";

import { scrollToTop } from "../../helper-functions/shared/pageUtils";
import { executeFunctionsAfterConnectionEstablished, executeFunctionsAfterPageLoad } from "../../helper-functions/api/apiUtils";
import { queryMenu } from "../../helper-functions/api/menuQueries";
import { prefsAreComplete } from "../../helper-functions/shared/guestPrefs";

/**
 * Shell: drawer + routes for menu, chat, login, admin.
 * First-visit welcome prefs gate the main chrome until completed.
 */
export default function MainPage(props) {
    const prefsReady = prefsAreComplete(props.prefs?.guestPrefs);
    const path = props.browser.location?.pathname || "/";
    const onWelcome = path === "/welcome";
    const showChrome = prefsReady && props.browser.showMainPageComponents && !onWelcome;

    React.useEffect(() => {
        executeFunctionsAfterPageLoad(props);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (props.server.isConnectionToServerEstablished) {
            executeFunctionsAfterConnectionEstablished(props);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.server.isConnectionToServerEstablished, props.auth.authStatus]);

    React.useEffect(() => {
        if (props.user.isLoggedIn && props.server.isConnectionToServerEstablished) {
            queryMenu(props);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.user.isLoggedIn]);

    React.useEffect(() => {
        scrollToTop();
    }, [props.browser.location.pathname]);

    React.useEffect(() => {
        if (!prefsReady && path !== "/welcome") {
            props.browser.navigate("/welcome");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prefsReady, path]);

    return (
        <Box sx={{ display: "flex" }}>
            {showChrome && (
                <>
                    <Appbar {...props} />
                    <AppDrawer {...props} />
                </>
            )}

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    height: "100vh",
                    overflow: "auto",
                    px: showChrome ? { xs: 1.5, sm: 3 } : 0,
                    pb: 0
                }}>
                {showChrome && <Toolbar />}

                <ErrorBoundary FallbackComponent={UnexpectedError}>
                    <Routes>
                        <Route
                            path="/welcome"
                            element={prefsReady ? <Navigate to="/" replace /> : <WelcomePrefs {...props} mode="welcome" />}
                        />
                        <Route
                            path="/preferences"
                            element={
                                prefsReady ? <WelcomePrefs {...props} mode="edit" /> : <Navigate to="/welcome" replace />
                            }
                        />
                        <Route path="/" element={prefsReady ? <MenuPage {...props} /> : <Navigate to="/welcome" replace />} />
                        <Route path="/chat" element={prefsReady ? <ChatPage {...props} /> : <Navigate to="/welcome" replace />} />
                        <Route
                            path="/favorites"
                            element={prefsReady ? <FavoritesPage {...props} /> : <Navigate to="/welcome" replace />}
                        />
                        <Route path="/login" element={<LoginPage {...props} />} />
                        <Route
                            path="/admin"
                            element={prefsReady ? <AdminSettings {...props} /> : <Navigate to="/welcome" replace />}
                        />
                        <Route path="/logout" element={<LogOut {...props} />} />
                        <Route path="/home" element={<Navigate to="/" replace />} />
                        <Route path="*" element={prefsReady ? <NoMatch {...props} /> : <Navigate to="/welcome" replace />} />
                    </Routes>
                </ErrorBoundary>

                {showChrome && <Copyright {...props} />}
            </Box>
        </Box>
    );
}
